import { JsonObject, JsonProperty } from "typescript-json-serializer";
import {ExtentConfiguration} from "./geometry-configuration";
import {BasemapConfiguration} from "./map-configuration";

@JsonObject()
export class PortalConfiguration {
    /**
     * The access level of the organization.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#access)
     */
    @JsonProperty()
    declare access: "public" | "private";

    /**
     * When `true`, access to the organization's Portal resources must occur over SSL.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#allSSL)
     */
    @JsonProperty()
    declare allSSL: boolean;

    /**
     * The authentication mode for handling authentication when the user attempts to access a secure resource.
     *
     * @default "auto"
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#authMode)
     */
    @JsonProperty()
    declare authMode?: "anonymous" | "auto" | "immediate" | "no-prompt";

    /**
     * Array of trusted servers to send credentials to when making Cross-Origin Resource Sharing (CORS) requests to
     * access services secured with web-tier authentication.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#authorizedCrossOriginDomains)
     */
    @JsonProperty()
    declare authorizedCrossOriginDomains: string[];

    /**
     * The query that defines the basemaps that should be displayed in the
     * [BasemapGallery](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-BasemapGallery.html)
     * when [useVectorBasemaps]
     * (https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#useVectorBasemaps)
     * is not true.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#basemapGalleryGroupQuery)
     */
    @JsonProperty()
    declare basemapGalleryGroupQuery: string;

    /**
     * The query that defines the 3D basemaps that should be displayed in the [BasemapGallery](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-BasemapGallery.html).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#basemapGalleryGroupQuery3D)
     */
    @JsonProperty()
    declare basemapGalleryGroupQuery3D?: string;

    /**
     * The Bing key to use for web maps using Bing Maps.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#bingKey)
     */
    @JsonProperty()
    declare bingKey: string;

    /**
     * Indicates whether an organization can list applications in the marketplace.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#canListApps)
     */
    @JsonProperty()
    declare canListApps: boolean;

    /**
     * Indicates whether an organization can list data services in the marketplace.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#canListData)
     */
    @JsonProperty()
    declare canListData: boolean;

    /**
     * Indicates whether an organization can list pre-provisioned items in the marketplace.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#canListPreProvisionedItems)
     */
    @JsonProperty()
    declare canListPreProvisionedItems: boolean;

    /**
     * Indicates whether an organization can provision direct purchases in the marketplace without customer request.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#canProvisionDirectPurchase)
     */
    @JsonProperty()
    declare canProvisionDirectPurchase: boolean;

    /**
     * When `true`, the organization's public items, groups and users are included in search queries.
     *
     * @default true
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#canSearchPublic)
     */
    @JsonProperty()
    declare canSearchPublic: boolean;

    /**
     * The Bing key can be shared to the public and is returned as part of a portal's description call (`/sharing/rest/portals/<orgid>`).
     *
     * @default false
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#canShareBingPublic)
     */
    @JsonProperty()
    declare canShareBingPublic: boolean;

    /**
     * When `true`, members of the organization can share resources outside the organization.
     *
     * @default false
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#canSharePublic)
     */
    @JsonProperty()
    declare canSharePublic: boolean;

    /**
     * Indicates whether to allow an organization with an enterprise IDP configured to be able to turn on or off the ArcGIS sign in.
     *
     * @default false
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#canSignInArcGIS)
     */
    @JsonProperty()
    declare canSignInArcGIS: boolean;

    /**
     * Indicates whether to allow an organization with an enterprise IDP configured to be able to turn on or off the enterprise sign in.
     *
     * @default false
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#canSignInIDP)
     */
    @JsonProperty()
    declare canSignInIDP: boolean;

    /**
     * The query that identifies the group containing the color sets used for rendering in the map viewer.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#colorSetsGroupQuery)
     */
    @JsonProperty()
    declare colorSetsGroupQuery: string;

    /**
     * Indicates whether to allow the organization to disable commenting.
     *
     * @default false
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#commentsEnabled)
     */
    @JsonProperty()
    declare commentsEnabled: boolean;

    /**
     * Date the organization was created.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#created)
     */
    @JsonProperty()
    declare created: Date;

    /**
     * The default locale (language and country) information.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#culture)
     */
    @JsonProperty()
    declare culture: string;

    /**
     * The custom base URL for the portal.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#customBaseUrl)
     */
    @JsonProperty()
    declare customBaseUrl: string;

    /**
     * The query that defines the default 3d basemap to use in scene views for the portal.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#default3DBasemapQuery)
     */
    @JsonProperty()
    declare default3DBasemapQuery?: string;

    /**
     * The default basemap to use for the portal.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#defaultBasemap)
     */
    @JsonProperty()
    declare defaultBasemap: BasemapConfiguration;
    /**
     * The default developer basemap to use for the portal when an [apiKey](https://developers.arcgis.com/javascript/latest/api-reference/esri-config.html#apiKey) is defined.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#defaultDevBasemap)
     */
    @JsonProperty()
    declare defaultDevBasemap?: BasemapConfiguration;

    /**
     * The default extent to use for the portal.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#defaultExtent)
     */
    @JsonProperty()
    declare defaultExtent: ExtentConfiguration;

    /**
     * The default vector basemap to use for the portal.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#defaultVectorBasemap)
     */
    @JsonProperty()
    declare defaultVectorBasemap: BasemapConfiguration;

    /**
     * A description of the organization/portal.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#description)
     */
    @JsonProperty()
    declare description: string;

    /**
     * The query that defines the basemaps that should be displayed in the [BasemapGallery](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-BasemapGallery.html) when an [apiKey](https://developers.arcgis.com/javascript/latest/api-reference/esri-config.html#apiKey) is defined.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#devBasemapGalleryGroupQuery)
     */
    @JsonProperty()
    declare devBasemapGalleryGroupQuery?: string;

    /**
     * Boolean value indicating whether to opt-in to the [Esri User Experience Improvement (EUEI) program](https://doc.arcgis.com/en/arcgis-online/reference/faq.htm#ESRI_QUESTIONANSWER_AED97F28DCD84F7682623C2FA9E5CE49).
     *
     * @default null
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#eueiEnabled)
     */
    @JsonProperty()
    declare eueiEnabled: boolean;

    /**
     * The featured groups for the portal.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#featuredGroups)
     */
    // @JsonProperty()
    // declare featuredGroups: PortalFeaturedGroups[];

    /**
     * The query that defines the featured group.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#featuredItemsGroupQuery)
     */
    @JsonProperty()
    declare featuredItemsGroupQuery: string;

    /**
     * The query that identifies the group containing features items for the gallery.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#galleryTemplatesGroupQuery)
     */
    @JsonProperty()
    declare galleryTemplatesGroupQuery: string;

    /**
     * Indicates whether the organization has content categories.
     *
     * @default false
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#hasCategorySchema)
     */
    @JsonProperty()
    declare hasCategorySchema: boolean;

    /**
     * Indicates whether the organization has classification schema.
     *
     * @default false
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#hasClassificationSchema)
     */
    @JsonProperty()
    declare hasClassificationSchema?: boolean;

    /**
     * This class contains properties to obtain information for various web services available on the portal.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#helperServices)
     */
    @JsonProperty()
    declare helperServices: any;

    /**
     * The group that contains featured content to be displayed on the home page.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#homePageFeaturedContent)
     */
    @JsonProperty()
    declare homePageFeaturedContent: string;

    /**
     * The number of featured items that can be displayed on the home page.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#homePageFeaturedContentCount)
     */
    @JsonProperty()
    declare homePageFeaturedContentCount: number;

    /**
     * The port used by the portal for HTTP communication.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#httpPort)
     */
    @JsonProperty()
    declare httpPort: number;

    /**
     * The port used by the portal for HTTPS communication.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#httpsPort)
     */
    @JsonProperty()
    declare httpsPort: number;

    /**
     * The id of the organization that owns this portal.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#id)
     */
    @JsonProperty()
    declare id: string;

    /**
     * The country code of the calling IP (ArcGIS Online only).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#ipCntryCode)
     */
    @JsonProperty()
    declare ipCntryCode: string;

    /**
     * Indicates whether the portal is an organization.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#isOrganization)
     */
    @JsonProperty()
    declare isOrganization: boolean;

    /**
     * Indicates if the portal is on-premises.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#isPortal)
     */
    @JsonProperty()
    declare isPortal: boolean;

    /**
     * Indicates if the portal is in read-only mode.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#isReadOnly)
     */
    @JsonProperty()
    declare isReadOnly: boolean;

    /**
     * The query that identifies the group containing editing templates.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#layerTemplatesGroupQuery)
     */
    @JsonProperty()
    declare layerTemplatesGroupQuery: string;

    /**
     * Indicates whether the portal's resources have loaded.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#loaded)
     */
    @JsonProperty()
    declare readonly loaded: boolean;

    /**
     * The maximum validity in minutes of tokens issued for users of the organization.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#maxTokenExpirationMinutes)
     */
    @JsonProperty()
    declare maxTokenExpirationMinutes: number;

    /**
     * Date the organization was last modified.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#modified)
     */
    @JsonProperty()
    declare modified: Date;

    /**
     * Name of the organization.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#name)
     */
    @JsonProperty()
    declare name: string;

    /**
     * URL of the portal host.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#portalHostname)
     */
    @JsonProperty()
    declare portalHostname: string;

    /**
     * The portal mode.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#portalMode)
     */
    @JsonProperty()
    declare portalMode: "multitenant" | "singletenant";

    /**
     * Properties specific to the organization, for example the "contact us" link.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#portalProperties)
     */
    @JsonProperty()
    declare portalProperties: any;

    /**
     * Indicates whether the recycle bin is enabled for the organization.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#recycleBinEnabled)
     */
    @JsonProperty()
    declare recycleBinEnabled?: boolean;

    /**
     * The region for the organization.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#region)
     */
    @JsonProperty()
    declare region: string;

    /**
     * The REST URL for the portal, for example "https://www.arcgis.com/sharing/rest" for ArcGIS Online and "https://www.example.com/arcgis/sharing/rest" for your in-house portal.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#restUrl)
     */
    @JsonProperty()
    declare readonly restUrl: string;

    /**
     * Custom HTML for the home page.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#rotatorPanels)
     */
    @JsonProperty()
    declare rotatorPanels: any[];

    /**
     * Indicates whether the description of your organization displays on the home page.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#showHomePageDescription)
     */
    @JsonProperty()
    declare showHomePageDescription: boolean;

    /**
     * The JSON used to create the property values when the `Portal` is loaded.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#sourceJSON)
     */
    @JsonProperty()
    declare sourceJSON: any;

    /**
     * Indicates whether hosted services are supported.
     *
     * @default false
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#supportsHostedServices)
     */
    @JsonProperty()
    declare supportsHostedServices: boolean;

    /**
     * The query that defines the symbols sets.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#symbolSetsGroupQuery)
     */
    @JsonProperty()
    declare symbolSetsGroupQuery: string;

    /**
     * The query that defines the collection of templates that will appear in the template gallery.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#templatesGroupQuery)
     */
    @JsonProperty()
    declare templatesGroupQuery: string;

    /**
     * The URL to the thumbnail of the organization.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#thumbnailUrl)
     */
    @JsonProperty()
    declare readonly thumbnailUrl: string;

    /**
     * Sets the units of measure for the organization's users.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#units)
     */
    @JsonProperty()
    declare units: "english" | "metric";

    /**
     * The URL to the portal instance.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#url)
     */
    @JsonProperty()
    declare url: string;

    /**
     * The prefix selected by the organization's administrator to be used with the customBaseURL.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#urlKey)
     */
    @JsonProperty()
    declare urlKey: string;

    /**
     * When `false`, 3D basemaps are hidden from the [BasemapGallery](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-BasemapGallery.html), regardless of the type of the view.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#use3dBasemaps)
     */
    @JsonProperty()
    declare use3dBasemaps?: boolean;

    /**
     * When `false`, The default 3d basemap is not used in the SceneViewer.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#useDefault3dBasemap)
     */
    @JsonProperty()
    declare useDefault3dBasemap?: boolean;

    /**
     * Information representing a registered user of the portal.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#user)
     */
    // @JsonProperty()
    // declare user: PortalUser;

    /**
     * When `true`, only simple where clauses that are compliant with SQL92 can be used when querying layers and tables.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#useStandardizedQuery)
     */
    @JsonProperty()
    declare useStandardizedQuery: boolean;

    /**
     * When `true`, the organization has opted in to use the vector tile basemaps, and (a) [vectorBasemapGalleryGroupQuery](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#vectorBasemapGalleryGroupQuery) should be used instead of [basemapGalleryGroupQuery](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#basemapGalleryGroupQuery), while (b) [defaultVectorBasemap](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#defaultVectorBasemap) should be used instead of [defaultBasemap](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#defaultBasemap).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#useVectorBasemaps)
     */
    @JsonProperty()
    declare useVectorBasemaps: boolean;

    /**
     * The query that defines the vector tiles basemaps that should be displayed in the [BasemapGallery](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-BasemapGallery.html) when [useVectorBasemaps](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#useVectorBasemaps) is true.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-Portal.html#vectorBasemapGalleryGroupQuery)
     */
    @JsonProperty()
    declare vectorBasemapGalleryGroupQuery: string;

    constructor() {}
}
